import React from "react";
import { useNavigate } from "react-router-dom";
import PublicHeader from "./PublicHeader";

const capabilityItems = [
  {
    title: "AI Translation",
    icon: "/Brain.svg",
    alt: "AI translation icon",
    description: "Translate content quickly when speed and efficiency matter.",
  },
  {
    title: "Complex File Support",
    icon: "/Files.svg",
    alt: "Complex file support icon",
    description: "Work with business and localization formats including DOCX, PDF, IDML, XLIFF, and more.",
  },
  {
    title: "Terminology Management",
    icon: "/BookOpen.svg",
    alt: "Terminology management icon",
    description: "Support approved terms and language preferences across projects.",
  },
  {
    title: "Optional Human Review",
    icon: "/UserCheck.svg",
    alt: "Optional human review icon",
    description: "Add professional review when quality, tone, or client delivery standards require it.",
  },
  {
    title: "Formatting Preservation",
    icon: "/FileText.svg",
    alt: "Formatting preservation icon",
    description: "Keep document structure and layout in mind from upload through delivery.",
  },
  {
    title: "Custom Workflows",
    icon: "/Workflow.svg",
    alt: "Custom workflows icon",
    description: "Choose language pairs, file types, review levels, QA, and formatting services.",
  },
];

const LoginForm = ({ onPendingConfirmation, onGoToRegister }) => {
  const navigate = useNavigate();


  const goToRegister = (e) => {
    if (e) e.preventDefault();
    if (onGoToRegister) {
      onGoToRegister();
      return;
    }
    navigate("/register");
  };

  const goToWorkflowBuilder = () => {
    window.location.href = "/workflow-builder";
  };

  return (
    <div className="mx-auto px-4">
      <PublicHeader />

      <div className="mx-auto w-full max-w-screen-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Option 1: Generic AI workflow */}
          <div
            role="button"
            tabIndex={0}
            onClick={goToRegister}
            onKeyDown={(e) => {
              if (e.key === "Enter") goToRegister(e);
            }}
            className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 text-left shadow-sm hover:shadow transition flex h-full flex-col cursor-pointer"
            aria-label="Start Fast AI Workflow"
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-10"
              style={{ backgroundColor: "#00a7e1" }}
            />
            <h2 className="text-lg font-semibold text-gray-900">
              Fast AI Workflow
            </h2>
            <p className="text-base text-gray-600 mt-2">
              Use a streamlined AI workflow for fast translations while preserving your document structure and layout.
            </p>
            <ul className="text-base text-gray-600 mt-3 list-disc list-outside pl-5 space-y-1">
              <li>Start translating in minutes</li>
              <li>Support file formats that ChatGPT, Google, and other AI tools don&apos;t support</li>
              <li>Preserve document structure and layout for complex files</li>
              <li>Upgrade to a custom translation workflow whenever your project grows</li>
            </ul>
            <div className="mt-auto pt-4">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goToRegister(e);
                }}
                className="text-white px-4 py-2 rounded w-full"
                style={{ backgroundColor: "#00a7e1" }}
              >
                Start AI Workflow
              </button>
            </div>
          </div>

          {/* Option 2: Custom workflow */}
          <div
            role="button"
            tabIndex={0}
            onClick={goToWorkflowBuilder}
            onKeyDown={(e) => {
              if (e.key === "Enter") goToWorkflowBuilder();
            }}
            className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 text-left shadow-sm hover:shadow transition flex h-full flex-col cursor-pointer"
            aria-label="Build Enterprise Translation Workflow"
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-10"
              style={{ backgroundColor: "#94c83d" }}
            />
            <h2 className="text-lg font-semibold text-gray-900">
              Enterprise Translation Workflow
            </h2>
            <p className="text-base text-gray-600 mt-2">
              Customize every stage of the translation process with enterprise-grade flexibility that&apos;s simple enough for small teams.
            </p>
            <ul className="text-base text-gray-700 mt-3 list-disc list-outside pl-5 space-y-1">
              <li>Language pairs and file types</li>
              <li>Terminology enforcement</li>
              <li>Professional human review (PEMT)</li>
              <li>QA and professional file formatting</li>
            </ul>
            <div className="mt-auto pt-4">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goToWorkflowBuilder();
                }}
                className="text-white px-4 py-2 rounded w-full"
                style={{ backgroundColor: "#94c83d" }}
              >
                Build Custom Workflow
              </button>
            </div>
          </div>
        </div>

            </div>

      {/* Product screenshots */}
      <section className="mt-14 -mx-4 bg-gray-50 border-y border-gray-100 py-12">
        <div className="mx-auto w-full max-w-screen-lg px-4">
          <div className="text-center mb-9">
            <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: "#00a7e1" }}>
              Product Preview
            </p>
            <h2 className="mt-2 text-2xl font-bold text-gray-900">
              Two Ways to Manage Your Translation Workflow
            </h2>
            <p className="mt-2 text-base text-gray-600 max-w-3xl mx-auto">
              Start quickly with AI translation or build a custom workflow with terminology, review, and formatting options.
            </p>
          </div>

          <div className="space-y-16">
            <div className="text-center">
              <div className="mb-5">
                <h3 className="text-xl font-semibold text-gray-900">
                  Fast AI Workflow
                </h3>
                <p className="mt-1 text-base text-gray-600 max-w-3xl mx-auto">
                  Upload your files, choose your languages, and translate complex documents while preserving formatting.
                </p>
              </div>
              <div className="flex justify-center">
                <img
                  src="/ZabFlowUI6.jpg"
                  alt="ZabFlow AI Fast AI Workflow screen showing language selection, file upload, selected files, credit estimate, and translate button"
                  className="w-full max-w-lg h-auto rounded-xl border border-gray-200 shadow-sm"
                />
              </div>
            </div>

            <div className="text-center">
              <div className="mb-5">
                <h3 className="text-xl font-semibold text-gray-900">
                  Enterprise Translation Workflow
                </h3>
                <p className="mt-1 text-base text-gray-600 max-w-3xl mx-auto">
                  Configure AI translation, terminology, human review, and post-formatting to match your organization&apos;s requirements.
                </p>
              </div>
              <div className="flex justify-center">
                <img
                  src="/WorkflowBuilderUI6.jpg"
                  alt="ZabFlow AI Custom Workflow Builder screen showing configurable post-AI translation review options"
                  className="w-full max-w-lg h-auto rounded-xl border border-gray-200 shadow-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-14 mx-auto w-full max-w-screen-lg">
        <div className="text-center mb-7">
          <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: "#94c83d" }}>
            Platform Capabilities
          </p>
          <h2 className="mt-2 text-2xl font-bold text-gray-900">
            Why ZabFlow AI?
          </h2>
          <p className="mt-2 text-base text-gray-600 max-w-3xl mx-auto">
            More than AI translation — ZabFlow AI helps manage the process around multilingual content.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {capabilityItems.map((item) => (
            <div
              key={item.title}
              className="rounded-xl border border-gray-200 bg-white p-5 text-center shadow-sm"
            >
              <img
                src={item.icon}
                alt={item.alt}
                className="mx-auto mb-3 h-10 w-10"
              />
              <h3 className="text-lg font-semibold text-gray-900">
                {item.title}
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Zab Translation Solutions credibility */}
      <section className="-mx-4 bg-slate-50 border-y border-gray-100 py-12">
        <div className="mx-auto w-full max-w-screen-lg px-4 text-center">
          <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: "#00a7e1" }}>
            Professional Foundation
          </p>
          <h2 className="mt-2 text-2xl font-bold text-gray-900">
            Built on Professional Translation Expertise
          </h2>
          <p className="mt-3 text-base text-gray-600 max-w-3xl mx-auto">
            ZabFlow AI was built by Zab Translation Solutions to combine modern AI workflows with practical translation operations, professional review options, and formatting support for real-world multilingual projects.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row justify-center gap-3">
            <a
              href="https://www.zabtranslation.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex justify-center rounded px-4 py-2 text-white"
              style={{ backgroundColor: "#00a7e1" }}
            >
              Visit Zab Translation Solutions
            </a>
            <a
              href="https://zabtranslation.com/request-a-free-quote.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex justify-center rounded px-4 py-2 text-white"
              style={{ backgroundColor: "#94c83d" }}
            >
              Request Professional Translation
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="-mx-4 bg-sky-50 border-y border-sky-100 py-12">
        <div className="mx-auto w-full max-w-screen-lg px-4 text-center">
          <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: "#00a7e1" }}>
            Ready to Try ZabFlow AI™?
          </p>
          <h2 className="mt-2 text-2xl font-bold text-gray-900">
            Start Your Free Trial
          </h2>
          <p className="mt-3 text-base text-gray-600 max-w-2xl mx-auto">
            Create your free account and receive 25 AI credits to experience the Fast AI Workflow. Translate real documents while preserving file structure and layout. No credit card required.
          </p>
          <button
            type="button"
            onClick={goToRegister}
            className="mt-5 inline-flex justify-center rounded px-6 py-3 text-white font-semibold"
            style={{ backgroundColor: "#00a7e1" }}
          >
            Start Free Trial
          </button>
        </div>
      </section>

      {/* Wide yellow warning */}
      <div className="mt-10 mx-auto w-full max-w-screen-lg mb-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-gray-800 leading-relaxed">
          <p>
            <b>AI workflows are designed for fast turnaround and internal or draft translations.</b> They can help when
            time constraints make it difficult to obtain a professional translation. While the goal is to produce
            understandable and reasonably formatted results, <b>translation accuracy, linguistic quality, and file formatting are not guaranteed</b>.
          </p>
          <p className="mt-3">
            For client-ready, publication-quality, or fully formatted translations, we strongly recommend{" "}
            <a href="/workflow-builder" className="underline" style={{ color: "#00a7e1" }}>
              building a custom translation workflow
            </a>{" "}
            with professional review and formatting or using{" "}
            <a
              href="https://zabtranslation.com/request-a-free-quote.html"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: "#00a7e1" }}
            >
              our professional translation services.
            </a>{" "}
          </p>
          <p className="mt-3 font-bold">
            For expert assistance, please contact{" "}
            <a href="mailto:request@zabtranslation.com" className="underline" style={{ color: "#00a7e1" }}>
              request@zabtranslation.com
            </a>{" "}
            or visit{" "}
            <a
              href="https://www.zabtranslation.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: "#00a7e1" }}
            >
              www.zabtranslation.com
            </a>{" "}
            for more information.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
